import { getDefaultCompetitionId, resolveCompetitionFilters } from '@statsbomb/kitbag-global-football/state';
import { KeysToCamelCase, CycleWithRels } from '@statsbomb/parachute-types';
import { Atom, atom, Getter } from 'jotai';
import { unwrap } from 'jotai/utils';
import { filterSetIdAtom } from '@/atoms/queryParams';
import { NumberOption } from '@/types/generic';
import { SectionAtomMap } from '@/types/section';
import { convertOptionsToValues } from '@/utils/array';
import { getAtomForSection } from '@/utils/atoms/section';
import { playerCompetitionOptionsAtom, playerCyclesOrderedAtom } from '../../queries/players';
import {
  teamCompetitionOptionsAtom,
  teamCyclesOrderedAtom,
  userDefaultTeamCyclesOrderedAtom,
} from '../../queries/teams';
import { eventDataCompetitionsOptionsAtom } from '../dataLocker/competitions';
import { filtersAtom } from '../filter';

const competitionSectionMapAtom = atom<SectionAtomMap<NumberOption[]>>(() => ({
  player: playerCompetitionOptionsAtom,
  team: teamCompetitionOptionsAtom,
  dataLocker: eventDataCompetitionsOptionsAtom,
  game: eventDataCompetitionsOptionsAtom,
}));

const defaultCompetitionSectionCycleMapAtom = atom<SectionAtomMap<KeysToCamelCase<CycleWithRels>[]>>(() => ({
  player: playerCyclesOrderedAtom,
  team: teamCyclesOrderedAtom,
  dataLocker: userDefaultTeamCyclesOrderedAtom,
  game: userDefaultTeamCyclesOrderedAtom,
}));

export const defaultCompetitionSectionCycleAtom = atom(getAtomForSection(defaultCompetitionSectionCycleMapAtom));

export const competitionOptionsAtom = atom(getAtomForSection(competitionSectionMapAtom));

const unwrappedCompetitionOptionsAtom = unwrap(competitionOptionsAtom, prev => prev || []);

const readCompetitionOptions = (get: Getter) => {
  try {
    return get(unwrappedCompetitionOptionsAtom);
  } catch {
    return [];
  }
};

export const defaultCompetitionIdAtom = atom(async get =>
  getDefaultCompetitionId(await get(defaultCompetitionSectionCycleAtom)),
);

const unwrappedDefaultCompetitionId = unwrap(defaultCompetitionIdAtom, prev => prev || null);

const readDefaultCompetitionId = (get: Getter) => {
  try {
    return get(unwrappedDefaultCompetitionId);
  } catch {
    return null;
  }
};

// Each atom keeps its own minimal dependency set (the filter-set guard must
// short-circuit before the option/default reads) so a rejection or update in
// one input only flushes through the atoms that genuinely read it.
const defaultCompetitionAtom = atom(get => {
  const filterSetId = get(filterSetIdAtom);
  /* we only want to do the default competitions logic below
  if we aren't coming from a filter set navigation */
  if (filterSetId && filterSetId !== 'unset') {
    return null;
  }

  return resolveCompetitionFilters({
    selectedCompetitionIds: null,
    competitionOptions: readCompetitionOptions(get),
    defaultCompetitionId: readDefaultCompetitionId(get),
  }).defaultCompetition;
});

export const selectedCompetitionsAtom = atom(get => {
  // Read the (rejectable) option load before the selection state, so a failed
  // load throws before this atom subscribes to the shared filter scope.
  const competitionOptions = readCompetitionOptions(get);
  const selectedCompetitionIds = get(filtersAtom).competitionIds;
  if (selectedCompetitionIds !== null) {
    return resolveCompetitionFilters({
      selectedCompetitionIds,
      competitionOptions,
      defaultCompetitionId: null,
    }).selectedCompetitions;
  }

  const defaultCompetition = get(defaultCompetitionAtom);
  return defaultCompetition ? [defaultCompetition] : [];
});

// when querying by gender, querying by competition gender is more reliable than player gender.
// There are places where the query doesn't support filtering by competition gender but we can still achieve
// the same result by using the competitions which are already filtered by gender, as the argument for the query
export const competitionIdsToFilterByAtom: Atom<number[]> = atom(get => {
  const selectedCompetitionsOptions = get(selectedCompetitionsAtom);
  // If there are no competitions selected by the user, we want to filter by all available competitions.
  // Why not just filter by nothing you might wonder? Because we only want to get back events which belong to the
  // gender that we're currently limiting to. And the way we do that is by filtering by the available competitions, since
  // only the competitions for the chosen gender will be available.
  const allCompetitionOptions = readCompetitionOptions(get);

  return convertOptionsToValues(
    selectedCompetitionsOptions.length ? selectedCompetitionsOptions : allCompetitionOptions,
  );
});

const competitionsLoadedAtom = atom(async get => {
  await get(competitionOptionsAtom);
  return true;
});

export const haveCompetitionOptionsLoadedAtom = unwrap(competitionsLoadedAtom, prev => prev || false);
